/**
 * My Work
 * The contractor's hired jobs, grouped by where each one stands.
 */

import { Hammer } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { QueryErrorState } from "@/components/ui/query-error-state";
import { Skeleton } from "@/components/ui/skeleton";
import { StatusBadge } from "@/components/ui/status-badge";
import {
	getContractorAssignments,
	type ProjectContractorItem,
	requestCompletion,
} from "@/features/marketplace/services/marketplace-api.service";
import {
	FloatPanel,
	PageHeader,
	Pill,
	type PillTone,
	StudioButton,
} from "@/features/studio/theme";
import { formatCalendarDate, formatPrice } from "@/lib/format";

type Stage = "onSite" | "awaitingSignOff" | "finished" | "other";

const STAGE_ORDER: Stage[] = ["onSite", "awaitingSignOff", "finished", "other"];

const STAGE_TONE: Record<Stage, PillTone> = {
	onSite: "active",
	awaitingSignOff: "awaiting",
	finished: "done",
	other: "neutral",
};

function stageOf(status: ProjectContractorItem["status"]): Stage {
	if (status === "hired") return "onSite";
	if (status === "completion_requested") return "awaitingSignOff";
	if (status === "completed") return "finished";
	return "other";
}

function JobRow({
	job,
	stage,
	requesting,
	onOpen,
	onRequestCompletion,
}: {
	job: ProjectContractorItem;
	stage: Stage;
	requesting: boolean;
	onOpen?: () => void;
	onRequestCompletion: () => void;
}) {
	const { t, i18n } = useTranslation();
	const locale = i18n.language === "hi" ? "hi-IN" : "en-IN";
	const dateOptions = {
		day: "numeric",
		month: "short",
		year: "numeric",
	} as const;
	const city = job.project?.property?.city;
	const facts = [
		city,
		job.quoteAmount != null
			? t("myWork.quoted", { amount: formatPrice(job.quoteAmount) })
			: null,
		stage === "finished" && job.completedAt
			? t("myWork.finishedOn", {
					date: formatCalendarDate(job.completedAt, dateOptions, locale),
				})
			: job.hiredAt
				? t("myWork.hiredOn", {
						date: formatCalendarDate(job.hiredAt, dateOptions, locale),
					})
				: null,
	].filter(Boolean);

	return (
		<li className="flex flex-col gap-3 py-4 sm:flex-row sm:items-center sm:gap-6">
			<div className="min-w-0 flex-1">
				{onOpen ? (
					<button
						type="button"
						onClick={onOpen}
						className="max-w-full truncate text-left font-display text-[17px] font-medium text-dashboard-text underline-offset-4 hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-dashboard-accent"
					>
						{job.project?.title || t("common.project")}
					</button>
				) : (
					<p className="truncate font-display text-[17px] font-medium text-dashboard-text">
						{job.project?.title || t("common.project")}
					</p>
				)}
				{facts.length > 0 && (
					<p className="mt-1 font-body text-[13px] text-dashboard-muted">
						{facts.join(", ")}
					</p>
				)}
				{stage === "awaitingSignOff" && (
					<p className="mt-1 font-body text-[13px] text-dashboard-muted-dark">
						{t("myWork.awaitingSignOffNote")}
					</p>
				)}
			</div>
			{stage === "onSite" && (
				<StudioButton
					variant="secondary"
					size="sm"
					onClick={onRequestCompletion}
					disabled={requesting}
					style={{ alignSelf: "flex-start" }}
				>
					{requesting ? t("myWork.marking") : t("myWork.markFinished")}
				</StudioButton>
			)}
			{stage === "other" && <StatusBadge status={job.status} />}
		</li>
	);
}

export function MyAssignmentsPage() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const [assignments, setAssignments] = useState<ProjectContractorItem[]>([]);
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState(false);
	const [requestingCompletion, setRequestingCompletion] = useState<
		string | null
	>(null);

	const loadAssignments = useCallback(async () => {
		setIsLoading(true);
		setError(false);
		try {
			setAssignments(await getContractorAssignments());
		} catch (e) {
			console.error("Failed to load assignments:", e);
			setError(true);
		} finally {
			setIsLoading(false);
		}
	}, []);

	useEffect(() => {
		loadAssignments();
	}, [loadAssignments]);

	const handleRequestCompletion = async (assignmentId: string) => {
		setRequestingCompletion(assignmentId);
		try {
			const result = await requestCompletion(assignmentId);
			if (result.success) {
				toast.success(t("myWork.markedFinished"));
				await loadAssignments();
			} else {
				toast.error(result.error ?? t("myWork.markFailed"));
			}
		} finally {
			setRequestingCompletion(null);
		}
	};

	const grouped = STAGE_ORDER.map((stage) => ({
		stage,
		jobs: assignments.filter((job) => stageOf(job.status) === stage),
	})).filter((group) => group.jobs.length > 0);

	return (
		<div className="mx-auto max-w-4xl space-y-8 px-4 py-6 sm:px-6 lg:py-10">
			<PageHeader
				title={t("myWork.title")}
				description={t("myWork.description")}
			/>

			{error ? (
				<FloatPanel elevation={1} padding={24}>
					<QueryErrorState
						message={t("myWork.loadError")}
						onRetry={loadAssignments}
					/>
				</FloatPanel>
			) : isLoading ? (
				<div className="space-y-4" data-testid="my-work-loading">
					<Skeleton className="h-32 w-full rounded-2xl" />
					<Skeleton className="h-32 w-full rounded-2xl" />
				</div>
			) : grouped.length === 0 ? (
				<FloatPanel elevation={1} padding={32}>
					<div className="mx-auto flex max-w-sm flex-col items-center text-center">
						<Hammer
							className="h-8 w-8 text-dashboard-muted-light"
							aria-hidden="true"
						/>
						<h2 className="mt-4 font-display text-xl font-medium text-dashboard-text">
							{t("myWork.emptyTitle")}
						</h2>
						<p className="mt-2 font-body text-sm text-dashboard-muted">
							{t("myWork.emptyBody")}
						</p>
						<StudioButton
							variant="primary"
							size="sm"
							onClick={() => navigate("/opportunities")}
							style={{ marginTop: 20 }}
						>
							{t("myWork.findWork")}
						</StudioButton>
					</div>
				</FloatPanel>
			) : (
				grouped.map(({ stage, jobs }) => (
					<section key={stage} aria-labelledby={`my-work-${stage}`}>
						<div className="mb-3 flex items-center gap-3">
							<h2
								id={`my-work-${stage}`}
								className="font-display text-xl font-medium text-dashboard-text"
							>
								{t(`myWork.stages.${stage}`)}
							</h2>
							<Pill tone={STAGE_TONE[stage]}>{jobs.length}</Pill>
						</div>
						<FloatPanel elevation={1} padding="0 24px">
							<ul className="divide-y divide-dashboard-divider">
								{jobs.map((job) => (
									<JobRow
										key={job.id}
										job={job}
										stage={stage}
										requesting={requestingCompletion === job.id}
										onOpen={
											job.project
												? () => navigate(`/projects/${job.project?.id}`)
												: undefined
										}
										onRequestCompletion={() => handleRequestCompletion(job.id)}
									/>
								))}
							</ul>
						</FloatPanel>
					</section>
				))
			)}
		</div>
	);
}
